// src/pages/HPExecutiveDashboard.jsx
// HP Executive Dashboard - CEO/GM Revenue Control View
// Pure Business KPIs • No Technical Details • Decision Surface
// Celerway Edge AI Platform
// v1.1 - Updated trend chart to show interventions

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

// ════════════════════════════════════════════════════════════════════════════
// MOCK EXECUTIVE DATA - Revenue & Business Metrics Only
// ════════════════════════════════════════════════════════════════════════════

const MOCK_EXECUTIVE_DATA = {
  // Revenue metrics
  revenueProtectedToday: 847500,
  revenueProtectedMTD: 12450000,
  revenueAccelerated: 340000,  // Day-1 activations, early completions
  revenueAtRisk72h: 28400,
  
  // Sites
  totalSites: 24,
  sitesOnline: 23,
  sitesAtRisk: 3,
  
  // Interventions
  interventionsToday: 27,
  interventionsMTD: 412,
  interventionSuccessRate: 98.4,
  
  // SLA
  slaComplianceRate: 99.2,
  slaMissesAverted: 14,
  
  // Trends (last 7 days) - AI interventions per day
  dailyInterventions: [
    { day: 'Mon', value: 24, revenue: 720000 },
    { day: 'Tue', value: 31, revenue: 890000 },
    { day: 'Wed', value: 22, revenue: 680000 },
    { day: 'Thu', value: 28, revenue: 815000 },
    { day: 'Fri', value: 35, revenue: 920000 },
    { day: 'Sat', value: 12, revenue: 245000 },
    { day: 'Sun', value: 8, revenue: 180000 },
  ],
  
  // Sites at risk (minimal detail)
  sitesAtRiskList: [
    { 
      id: 'london-hq',
      name: 'London HQ', 
      riskAmount: 12400,
      hoursToImpact: 18,
      riskReason: 'Supply chain',
    },
    { 
      id: 'manchester-dc',
      name: 'Manchester DC', 
      riskAmount: 8500,
      hoursToImpact: 36,
      riskReason: 'Network degradation',
    },
    { 
      id: 'birmingham-ops',
      name: 'Birmingham Ops', 
      riskAmount: 7500,
      hoursToImpact: 48,
      riskReason: 'Maintenance due',
    },
  ],
  
  // Recent interventions (anonymized)
  recentInterventions: [
    { type: 'Traffic Reroute', outcome: 'Protected', value: 4500, timeAgo: '12 min' },
    { type: 'Priority Boost', outcome: 'Protected', value: 8200, timeAgo: '28 min' },
    { type: 'Auto Supply Order', outcome: 'Averted', value: 15600, timeAgo: '1 hr' },
    { type: 'Load Balance', outcome: 'Protected', value: 3200, timeAgo: '2 hr' },
    { type: 'Session Preserve', outcome: 'Protected', value: 6800, timeAgo: '3 hr' },
  ],
};

// ════════════════════════════════════════════════════════════════════════════
// EXECUTIVE KPI TILE COMPONENT
// ════════════════════════════════════════════════════════════════════════════

const KPITile = ({ 
  title, 
  value, 
  subtitle, 
  trend, 
  trendDirection, 
  accentColor = 'cyan',
  icon,
  size = 'normal',
  onClick,
}) => {
  const colorClasses = {
    cyan: {
      gradient: 'from-cyan-500/20 to-blue-500/20',
      border: 'border-cyan-500/30',
      text: 'text-cyan-300',
      accent: 'bg-cyan-500',
      glow: 'shadow-cyan-500/20',
    },
    emerald: {
      gradient: 'from-emerald-500/20 to-teal-500/20',
      border: 'border-emerald-500/30',
      text: 'text-emerald-300',
      accent: 'bg-emerald-500',
      glow: 'shadow-emerald-500/20',
    },
    amber: {
      gradient: 'from-amber-500/20 to-orange-500/20',
      border: 'border-amber-500/30',
      text: 'text-amber-300',
      accent: 'bg-amber-500',
      glow: 'shadow-amber-500/20',
    },
    blue: {
      gradient: 'from-blue-500/20 to-indigo-500/20',
      border: 'border-blue-500/30',
      text: 'text-blue-300',
      accent: 'bg-blue-500',
      glow: 'shadow-blue-500/20',
    },
    red: {
      gradient: 'from-red-500/20 to-rose-500/20',
      border: 'border-red-500/30',
      text: 'text-red-300',
      accent: 'bg-red-500',
      glow: 'shadow-red-500/20',
    },
  };
  
  const colors = colorClasses[accentColor] || colorClasses.cyan;
  
  return (
    <div 
      className={`relative bg-gradient-to-br ${colors.gradient} border ${colors.border} rounded-2xl p-6 shadow-xl ${colors.glow} transition-all duration-300 hover:scale-[1.02] ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
    >
      {/* Top accent bar */}
      <div className={`absolute top-0 left-6 right-6 h-1 ${colors.accent} rounded-b-full`} />
      
      {/* Icon */}
      {icon && (
        <div className="absolute top-4 right-4 text-2xl opacity-80">
          {icon}
        </div>
      )}
      
      {/* Title */}
      <div className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-2">
        {title}
      </div>
      
      {/* Value */}
      <div className={`${size === 'large' ? 'text-4xl' : 'text-3xl'} font-bold ${colors.text} mb-1`}>
        {value}
      </div>
      
      {/* Subtitle */}
      {subtitle && (
        <div className="text-sm text-slate-400">
          {subtitle}
        </div>
      )}
      
      {/* Trend */}
      {trend && (
        <div className={`mt-3 flex items-center gap-1 text-xs ${
          trendDirection === 'up' ? 'text-emerald-400' : 
          trendDirection === 'down' ? 'text-red-400' : 'text-slate-400'
        }`}>
          {trendDirection === 'up' && '↑'}
          {trendDirection === 'down' && '↓'}
          {trend}
        </div>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// SITES AT RISK COMPONENT (Minimal)
// ════════════════════════════════════════════════════════════════════════════

const SitesAtRiskCard = ({ sites, onViewDetails }) => {
  if (sites.length === 0) {
    return (
      <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-6 text-center">
        <div className="text-4xl mb-2">✓</div>
        <div className="text-xl font-bold text-emerald-300">All Sites Secure</div>
        <div className="text-sm text-slate-400">No predicted revenue impact</div>
      </div>
    );
  }
  
  const totalRisk = sites.reduce((sum, s) => sum + s.riskAmount, 0);
  
  return (
    <div className="bg-gradient-to-br from-amber-500/10 to-red-500/10 border border-amber-500/30 rounded-2xl p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-xs font-medium text-slate-400 uppercase tracking-wider">Sites Requiring Attention</div>
          <div className="text-2xl font-bold text-amber-300 mt-1">${totalRisk.toLocaleString()} at risk</div>
        </div>
        <div className="text-3xl">⚠️</div>
      </div>
      
      <div className="space-y-3">
        {sites.map((site, idx) => (
          <div 
            key={site.id}
            className="bg-slate-800/50 rounded-xl p-4 flex items-center justify-between"
          >
            <div>
              <div className="font-semibold text-white">{site.name}</div>
              <div className="text-xs text-slate-400">{site.riskReason} • Impact in {site.hoursToImpact}h</div>
            </div>
            <div className="text-right">
              <div className="font-bold text-amber-300">${site.riskAmount.toLocaleString()}</div>
            </div>
          </div>
        ))}
      </div>
      
      <button 
        onClick={onViewDetails}
        className="mt-4 w-full py-2 bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/30 rounded-lg text-amber-300 text-sm font-medium transition-colors"
      >
        View Control Panel →
      </button>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// RECENT INTERVENTIONS COMPONENT
// ════════════════════════════════════════════════════════════════════════════

const RecentInterventions = ({ interventions }) => (
  <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-500/30 rounded-2xl p-6">
    <div className="flex items-center justify-between mb-4">
      <div>
        <div className="text-xs font-medium text-slate-400 uppercase tracking-wider">Recent AI Actions</div>
        <div className="text-lg font-semibold text-white mt-1">Autonomous Interventions</div>
      </div>
      <div className="text-2xl">🤖</div>
    </div>
    
    <div className="space-y-2">
      {interventions.map((intervention, idx) => (
        <div 
          key={idx}
          className="flex items-center justify-between py-2 border-b border-slate-700/50 last:border-0"
        >
          <div className="flex items-center gap-3">
            <div className={`w-2 h-2 rounded-full ${
              intervention.outcome === 'Protected' ? 'bg-blue-400' : 'bg-emerald-400'
            }`} />
            <div>
              <div className="text-sm text-white">{intervention.type}</div>
              <div className="text-xs text-slate-500">{intervention.timeAgo}</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm font-semibold text-emerald-300">${intervention.value.toLocaleString()}</div>
            <div className="text-xs text-slate-500">{intervention.outcome}</div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ════════════════════════════════════════════════════════════════════════════
// MINI TREND CHART
// ════════════════════════════════════════════════════════════════════════════

const MiniTrendChart = ({ data, label }) => {
  const maxValue = Math.max(...data.map(d => d.value));
  const totalInterventions = data.reduce((sum, d) => sum + d.value, 0);
  const totalRevenue = data.reduce((sum, d) => sum + (d.revenue || 0), 0);
  
  return (
    <div className="bg-gradient-to-br from-slate-700/30 to-slate-800/30 border border-slate-600/30 rounded-2xl p-6">
      <div className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-2">{label}</div>
      <div className="text-lg font-bold text-cyan-300 mb-4">{totalInterventions} interventions</div>
      
      <div className="flex items-end gap-2 h-20">
        {data.map((item, idx) => {
          const height = (item.value / maxValue) * 100;
          return (
            <div key={idx} className="flex-1 flex flex-col items-center group">
              <div className="text-[9px] text-cyan-400 opacity-0 group-hover:opacity-100 transition-opacity mb-1">
                {item.value}
              </div>
              <div 
                className="w-full bg-gradient-to-t from-cyan-500 to-cyan-400 rounded-t-sm transition-all duration-300 hover:from-cyan-400 hover:to-cyan-300"
                style={{ height: `${height}%`, minHeight: '4px' }}
              />
              <div className="text-[10px] text-slate-500 mt-2">{item.day}</div>
            </div>
          );
        })}
      </div>
      
      <div className="mt-4 pt-4 border-t border-slate-700/50 flex items-center justify-between">
        <span className="text-xs text-slate-500">${(totalRevenue / 1000000).toFixed(1)}M protected</span>
        <span className="text-xs text-emerald-400">↑ 12% vs last week</span>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// MAIN EXECUTIVE DASHBOARD
// ════════════════════════════════════════════════════════════════════════════

const HPExecutiveDashboard = () => {
  const [data] = useState(MOCK_EXECUTIVE_DATA);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // Simulate loading
  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 800);
    return () => clearTimeout(timer);
  }, []);
  
  // Update time
  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(interval);
  }, []);
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-cyan-400/30 border-t-cyan-400 rounded-full animate-spin mx-auto mb-4" />
          <div className="text-cyan-300 text-lg font-medium">Loading Executive View...</div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <span className="w-12 h-12 bg-gradient-to-br from-blue-500/30 to-cyan-600/30 rounded-xl flex items-center justify-center border border-blue-400/30">
              <span className="text-2xl">📊</span>
            </span>
            HP Revenue Control
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            Executive Dashboard • {currentTime.toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="text-xs text-slate-500 uppercase">Last Updated</div>
            <div className="text-sm text-slate-300">
              {currentTime.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-emerald-500/20 border border-emerald-500/30 rounded-lg">
            <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
            <span className="text-emerald-300 text-sm font-medium">System Active</span>
          </div>
          <Link
            to="/hp-revenue-control"
            className="px-4 py-2 bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-500/30 rounded-lg text-cyan-300 text-sm font-medium transition-colors"
          >
            Operations View →
          </Link>
        </div>
      </div>
      
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* PRIMARY KPIs - The Only 4 Things That Matter */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      
      <div className="grid grid-cols-4 gap-6 mb-8">
        {/* Revenue Protected Today */}
        <KPITile
          title="Revenue Protected"
          value={`$${Math.floor(data.revenueProtectedToday / 1000).toLocaleString()}K`}
          subtitle="Today"
          trend="vs $720K yesterday"
          trendDirection="up"
          accentColor="emerald"
          icon="💰"
          size="large"
        />
        
        {/* Revenue Accelerated */}
        <KPITile
          title="Revenue Accelerated"
          value={`$${Math.floor(data.revenueAccelerated / 1000).toLocaleString()}K`}
          subtitle="Day-1 activations recovered"
          trend="14 early completions"
          trendDirection="up"
          accentColor="blue"
          icon="🚀"
        />
        
        {/* Sites at Risk */}
        <KPITile
          title="Sites at Risk"
          value={data.sitesAtRisk}
          subtitle="Next 72 hours"
          trend={`$${data.revenueAtRisk72h.toLocaleString()} exposure`}
          trendDirection={data.sitesAtRisk > 0 ? 'down' : 'up'}
          accentColor={data.sitesAtRisk > 0 ? 'amber' : 'emerald'}
          icon="⚠️"
        />
        
        {/* Autonomous Interventions */}
        <KPITile
          title="Edge Interventions"
          value={data.interventionsToday}
          subtitle="Executed today"
          trend={`${data.interventionSuccessRate}% success rate`}
          trendDirection="up"
          accentColor="cyan"
          icon="🤖"
        />
      </div>
      
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* SECONDARY PANELS */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      
      <div className="grid grid-cols-3 gap-6 mb-8">
        {/* Sites at Risk Detail */}
        <SitesAtRiskCard 
          sites={data.sitesAtRiskList}
          onViewDetails={() => {
            // Navigate to operations view
            window.location.href = '/hp-revenue-control';
          }}
        />
        
        {/* Recent Interventions */}
        <RecentInterventions interventions={data.recentInterventions} />
        
        {/* Weekly Trend */}
        <MiniTrendChart 
          data={data.dailyInterventions}
          label="This Week"
        />
      </div>
      
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* SUMMARY ROW */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
          <div className="text-xs text-slate-500 uppercase">Month to Date</div>
          <div className="text-2xl font-bold text-emerald-300 mt-1">
            ${(data.revenueProtectedMTD / 1000000).toFixed(1)}M
          </div>
          <div className="text-xs text-slate-400">Revenue protected</div>
        </div>
        
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
          <div className="text-xs text-slate-500 uppercase">Fleet Status</div>
          <div className="text-2xl font-bold text-blue-300 mt-1">
            {data.sitesOnline}/{data.totalSites}
          </div>
          <div className="text-xs text-slate-400">Sites online</div>
        </div>
        
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
          <div className="text-xs text-slate-500 uppercase">SLA Compliance</div>
          <div className="text-2xl font-bold text-emerald-300 mt-1">
            {data.slaComplianceRate}%
          </div>
          <div className="text-xs text-slate-400">{data.slaMissesAverted} misses averted</div>
        </div>
        
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
          <div className="text-xs text-slate-500 uppercase">This Month</div>
          <div className="text-2xl font-bold text-cyan-300 mt-1">
            {data.interventionsMTD}
          </div>
          <div className="text-xs text-slate-400">Autonomous actions</div>
        </div>
      </div>
      
      {/* Footer */}
      <div className="mt-8 pt-6 border-t border-slate-700/50 flex items-center justify-between text-xs text-slate-500">
        <div className="flex items-center gap-4">
          <span>Celerway Edge Control Plane</span>
          <span className="text-slate-600">•</span>
          <span>HP Smart Device Services</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-cyan-400">●</span>
          <span>Real-time data • Auto-refresh: 60s</span>
        </div>
      </div>
    </div>
  );
};

export default HPExecutiveDashboard;
